import { AppealHistoryEvent } from "../../../../data-model/appealDetail";
import { PocAssignee, PocAssigneeLabels } from "../../../../enums/pocAssignee";

const APPEAL_EVENT_TYPE_LABELS: Record<string, string> = {
  letter_generated: "Letter Generated",
  case_sent_to_appeal: "Case Sent to Appeal",
  appeal_not_required: "Appeal Not Required",
  fields_updated: "Fields Updated",
  packet_previewed: "Packet Previewed",
  appeal_submitted: "Appeal Submitted",
  fax_status_updated: "Fax Status Updated",
};

/** Bold-capable fragment for a timeline action sentence. */
export interface AppealHistoryTextSegment {
  text: string;
  bold?: boolean;
}

/** View model for one appeal history timeline row. */
export interface AppealHistoryDisplayEntry {
  timeLabel: string;
  dateLabel: string;
  actorName: string;
  actionSegments: AppealHistoryTextSegment[];
  detailNote?: string;
}

/**
 * Maps a backend appeal event type to a display label.
 *
 * @param eventType - Raw event type from `appeal_events`
 * @returns Human-readable event label
 */
export const formatAppealEventType = (eventType: string): string => {
  const trimmed = eventType.trim();
  if (!trimmed) {
    return "Event";
  }

  return (
    APPEAL_EVENT_TYPE_LABELS[trimmed] ??
    trimmed.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
  );
};

/**
 * Formats an actor email or id into a display name for the timeline header.
 *
 * @param actor - Actor from the appeal event row
 * @returns Display name shown on the right side of the timeline header
 */
export const formatAppealHistoryActorName = (
  actor: string | null | undefined,
): string => {
  const trimmed = actor?.trim();
  if (!trimmed) {
    return "System";
  }

  if (trimmed.includes("@")) {
    const localPart = trimmed.split("@")[0] ?? trimmed;
    return localPart.charAt(0).toUpperCase() + localPart.slice(1);
  }

  return PocAssigneeLabels[trimmed as PocAssignee] ?? trimmed;
};

/**
 * Splits an appeal event timestamp into separate time and date labels.
 *
 * @param value - ISO timestamp or date string
 * @returns Time and date strings for the timeline header
 */
export const splitAppealHistoryTimestamp = (
  value: string | null | undefined,
): { timeLabel: string; dateLabel: string } => {
  if (!value?.trim()) {
    return { timeLabel: "--", dateLabel: "--" };
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { timeLabel: "--", dateLabel: "--" };
  }

  const timeLabel = date
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace(/\s/g, "");

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);

  return {
    timeLabel,
    dateLabel: `${month}/${day}/${year}`,
  };
};

/**
 * Parses `Updated key=value, ...` detail strings from appeal update events.
 *
 * @param detail - Event detail string from the backend
 * @returns Parsed field map
 */
const parseUpdatedFields = (detail: string): Record<string, string> => {
  const match = detail.match(/^Updated\s+(.+)$/i);
  if (!match?.[1]) {
    return {};
  }

  const fields: Record<string, string> = {};
  for (const part of match[1].split(",")) {
    const separatorIndex = part.indexOf("=");
    if (separatorIndex === -1) {
      continue;
    }

    const key = part.slice(0, separatorIndex).trim();
    const value = part.slice(separatorIndex + 1).trim();
    if (key) {
      fields[key] = value;
    }
  }

  return fields;
};

/**
 * Resolves a POC id or raw value to a display label.
 *
 * @param value - POC id or name from an update event
 * @returns Human-readable POC label
 */
const resolvePocLabel = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return "Unassigned";
  }

  return (
    PocAssigneeLabels[trimmed as PocAssignee] ??
    trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
  );
};

/**
 * Builds the timeline copy and optional note box for one appeal history event.
 *
 * @param event - Raw event from `pharmacy/appeal/detail`
 * @returns Display model for the timeline row
 */
export const buildAppealHistoryDisplayEntry = (
  event: AppealHistoryEvent,
): AppealHistoryDisplayEntry => {
  const actorName = formatAppealHistoryActorName(event.actor);
  const { timeLabel, dateLabel } = splitAppealHistoryTimestamp(event.created_at);
  const detail = event.detail?.trim() ?? "";
  const eventType = event.event_type?.trim() ?? "";

  if (eventType === "fields_updated") {
    const fields = parseUpdatedFields(detail);

    if (fields.appeal_poc) {
      return {
        timeLabel,
        dateLabel,
        actorName,
        actionSegments: [
          { text: "Order Assigned to " },
          { text: resolvePocLabel(fields.appeal_poc), bold: true },
        ],
        detailNote: fields.appeal_comment || undefined,
      };
    }

    if (fields.appeal_comment) {
      return {
        timeLabel,
        dateLabel,
        actorName,
        actionSegments: [
          { text: `${actorName} has ` },
          { text: "updated appeal comments", bold: true },
        ],
        detailNote: fields.appeal_comment,
      };
    }

    return {
      timeLabel,
      dateLabel,
      actorName,
      actionSegments: [
        { text: `${actorName} has ` },
        { text: "updated appeal info", bold: true },
      ],
    };
  }

  if (eventType === "fax_status_updated") {
    const faxDetail = detail.replace(/^Fax status:\s*/i, "").trim();
    return {
      timeLabel,
      dateLabel,
      actorName,
      actionSegments: [
        { text: `${actorName} has ` },
        { text: "checked Fax", bold: true },
      ],
      detailNote: faxDetail || "No information received yet",
    };
  }

  if (eventType === "case_sent_to_appeal") {
    return {
      timeLabel,
      dateLabel,
      actorName,
      actionSegments: [
        { text: `${actorName} has ` },
        { text: "sent case to appeal", bold: true },
      ],
    };
  }

  if (eventType === "appeal_submitted") {
    return {
      timeLabel,
      dateLabel,
      actorName,
      actionSegments: [
        { text: `${actorName} has ` },
        { text: "submitted appeal", bold: true },
      ],
      detailNote: detail || undefined,
    };
  }

  if (eventType === "appeal_not_required") {
    const reason = detail.replace(/^Appeal not required:\s*/i, "").trim();
    return {
      timeLabel,
      dateLabel,
      actorName,
      actionSegments: [
        { text: `${actorName} has ` },
        { text: "marked appeal not required", bold: true },
      ],
      detailNote: reason || undefined,
    };
  }

  const actionLabel = formatAppealEventType(eventType).toLowerCase();
  return {
    timeLabel,
    dateLabel,
    actorName,
    actionSegments: [
      { text: `${actorName} has ` },
      { text: actionLabel, bold: true },
    ],
    detailNote: detail && !detail.startsWith("Updated ") ? detail : undefined,
  };
};

/**
 * Normalizes appeal history events for chronological timeline rendering.
 *
 * @param events - Raw events from `pharmacy/appeal/detail`
 * @returns Events sorted oldest-first
 */
export const normalizeAppealHistoryEvents = (
  events: AppealHistoryEvent[] | undefined,
): AppealHistoryEvent[] => {
  if (!events?.length) {
    return [];
  }

  return [...events].sort((left, right) => {
    const leftTime = left.created_at ? new Date(left.created_at).getTime() : 0;
    const rightTime = right.created_at ? new Date(right.created_at).getTime() : 0;
    return leftTime - rightTime;
  });
};

/**
 * Converts normalized appeal events into timeline display entries.
 *
 * @param events - Chronologically sorted appeal events
 * @returns Timeline view models
 */
export const buildAppealHistoryTimelineEntries = (
  events: AppealHistoryEvent[],
): AppealHistoryDisplayEntry[] =>
  events.map((event) => buildAppealHistoryDisplayEntry(event));
